import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store.js';
import { index } from '../worker/client.js';
import type { SearchResult } from '../worker/api.js';
import { usePaneState } from './paneState.js';
import { useIndexRev } from './hooks.js';
import { highlightTerms, markMatches } from './highlight.jsx';

export function SearchPane({ initialQuery }: { initialQuery?: string }) {
  const openNote = useApp((s) => s.openNote);
  const rev = useIndexRev();
  const [query, setQuery] = useState(() => initialQuery ?? usePaneState.getState().searchQuery);
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const lastPushRev = useRef(usePaneState.getState().searchRev);
  const inputRef = useRef<HTMLInputElement>(null);

  // Adopt queries pushed from other panes (tag clicks, graph filters).
  useEffect(
    () =>
      usePaneState.subscribe((s) => {
        if (s.searchRev !== lastPushRev.current) {
          lastPushRev.current = s.searchRev;
          setQuery(s.searchQuery);
        }
      }),
    [],
  );

  // Debounced search; re-runs when the index changes underneath us.
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    let stale = false;
    const t = setTimeout(() => {
      void index.search(q, 100).then((r) => {
        if (stale) return;
        setResults(r);
        setBusy(false);
      });
    }, 200);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [query, rev]);

  const terms = useMemo(() => highlightTerms(query), [query]);
  const empty = !query.trim();

  return (
    <div className="pane">
      <div className="search-box">
        <input
          ref={inputRef}
          className="pane-input"
          type="search"
          placeholder="Search notes…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
        />
        {query && (
          <button
            className="search-clear"
            title="Clear search"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        )}
      </div>

      {empty && (
        <div className="search-hints">
          <div className="hint-title">Search the whole vault. Operators:</div>
          <div className="hint">
            <code>tag:project</code> notes with a tag (nested tags match)
          </div>
          <div className="hint">
            <code>path:folder</code> restrict to a folder
          </div>
          <div className="hint">
            <code>&quot;exact phrase&quot;</code> match a phrase
          </div>
        </div>
      )}

      {!empty && busy && results === null && <div className="spinner" />}

      {!empty && results !== null && results.length === 0 && (
        <div className="pane-empty">No results for “{query.trim()}”.</div>
      )}

      {!empty && results !== null && results.length > 0 && (
        <div className="result-list">
          {results.map((r) => (
            <button key={r.id} className="result-row" onClick={() => openNote(r.id)}>
              <span className="result-title">{markMatches(r.title, terms)}</span>
              <span className="result-path">{r.path}</span>
              {r.snippet && (
                <span className="result-snippet">{markMatches(r.snippet, terms)}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
